import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import {
  ASSET_V2_ENDPOINT,
  CASES,
  CASES_ACTIONS,
  CASES_ALARMS,
  CASES_BY_ID,
  CASES_DIAGNOSIS,
  CASES_FEEDBACK,
  CASES_FILTERS_OPTIONS,
  CASES_OPTIONS,
  CASES_OWNERS,
  TEMPLATE_OPTIONS,
  WORKSPACES_CASES,
} from 'config/endpoints';
import { Classification, Status } from 'queries/assets/contract';
import { EventItem, GenericQueryOptions } from 'queries/events/types';
import { get, patch, post, put } from 'utils/axios';
import { actionsFilter } from 'utils/CaseUtils';
import {
  Action,
  ActionsArray,
  ActiveTab,
  CaseCreatePayload,
  CaseForm,
  CaseListItem,
  CaseOptions,
  CasePatchPayload,
  CasePayload,
  CasesFilterOptionsResponse,
  caseTabs,
  UpdateActionsPayload,
  UpdateDiagnosisPayload,
  UpdateFeedbackPayload,
} from './types';
import { useFeatureFlag } from '@shape/feature-flag';

export const useInvalidateCaseAlarmsQueries = () => {
  const queryClient = useQueryClient();

  return (id?: string) => {
    queryClient.invalidateQueries(['GET_CASE_ALARMS', id]);
  };
};

export const useInvalidateCasesQueries = () => {
  const queryClient = useQueryClient();

  return (id?: string) => {
    queryClient.invalidateQueries(['GET_CASES']);
    queryClient.invalidateQueries(['GET_CASE', id]);
  };
};

export type CaseFilters = {
  activeTab?: ActiveTab;
  assets?: { id: string; name: string }[];
  category?: string[];
  assets_id?: string[];
  month?: number;
  year?: number;
};

export const useCasesQuery = ({
  filters,
  queryOptions,
}: {
  filters: CaseFilters;
  queryOptions?: GenericQueryOptions<{ cases: CaseListItem[] }>;
}) => {
  const enableCases = useFeatureFlag('feat-cases');
  const { activeTab, assets, assets_id, month, year, category } = filters;
  const ancestor_asset_ids =
    assets && assets.length > 0 ? assets.map((asset) => asset.id)?.join(',') : undefined;

  const response = useQuery(
    [
      'GET_CASES',
      'MONITORING_LOADING',
      { activeTab, ancestor_asset_ids, assets_id, category, month, year },
    ],
    get<
      { cases: CaseListItem[] },
      {
        opened?: boolean;
        ancestor_asset_ids?: string;
        assets_id?: string;
        assets_categories?: string;
        month?: number;
        year?: number;
      }
    >(WORKSPACES_CASES(), {
      opened: activeTab === caseTabs.all ? undefined : activeTab === caseTabs.open,
      ancestor_asset_ids,
      assets_categories: category?.join(','),
      assets_id: assets_id?.join(','),
      month,
      year,
    }),
    {
      retry: false,
      enabled: enableCases,
      ...queryOptions,
    },
  );

  return {
    ...response,
    data: response.data?.cases || [],
  };
};

export const useCasesDataByAssetId = (assetId: string) => {
  return useQuery(
    ['GET_CASES_BY_ASSET_ID', assetId],
    get<{ cases: CaseListItem[] }>(WORKSPACES_CASES()), //trocar endpoint,
    {
      retry: false,
    },
  );
};

export const useDiagnosisQuery = (caseId?: string) => {
  const { data: actionsData } = useGetAllActionsQuery(caseId);
  const { data: alarmsData } = useCaseRelatedAlarms({ id: caseId });

  return useQuery(['GET_CASE', caseId], get<CaseForm>(CASES_BY_ID(caseId)), {
    retry: false,
    enabled: !!caseId,
    select: (data: CaseForm) => {
      return {
        ...data,
        actions: actionsData ?? [],
        alarms: alarmsData?.alarms ?? [],
      };
    },
  });
};

const createCase = (payload: CasePayload) => {
  const { alarms, asset_id, name, is_able_to_operate, owner_mail, savings } = payload;

  return post<CaseCreatePayload, { id: string }>(WORKSPACES_CASES(), {
    payload: {
      alarms,
      name,
      is_able_to_operate,
      owner_mail,
      asset_id,
      anomaly_start: payload.anomaly_start,
      diagnosis: payload.diagnosis,
      dimension: payload.dimension,
      discipline: payload.discipline,
      severity: payload.severity,
      status: payload.status,
      detection_method: payload.detection_method,
      savings,
    },
  });
};

const updateCase = (payload: Partial<CasePayload>) => {
  const { alarms, asset_id, id, is_able_to_operate, name, owner_mail, savings } = payload;

  return patch<Omit<CasePatchPayload, 'id'>, AxiosResponse>(CASES(id), {
    payload: {
      alarms,
      asset_id,
      is_able_to_operate,
      name,
      owner_mail,
      savings,
    },
  });
};

const saveDiagnosis = ({
  alarms,
  name,
  asset_id,
  status,
  owner_mail,
  id = '',
  is_able_to_operate,
  severity,
  detection_method,
  diagnosis,
  dimension,
  discipline,
  anomaly_start,
  savings,
}: CasePayload) => {
  return put<UpdateDiagnosisPayload, AxiosResponse>(CASES_DIAGNOSIS(id), {
    payload: {
      name,
      alarms,
      asset_id,
      discipline,
      is_able_to_operate,
      status,
      owner_mail,
      severity,
      diagnosis,
      detection_method,
      dimension,
      anomaly_start,
      savings,
    },
  });
};

const saveActions = ({
  severity,
  detection_method,
  diagnosis,
  dimension,
  discipline,
  anomaly_start,
  alarms,
  name,
  asset_id,
  owner_mail,
  status,
  is_able_to_operate,
  id = '',
  actions,
  savings,
}: Partial<CasePayload>) => {
  const new_actions: Action[] = [];
  const update_actions: Action[] = [];

  for (const action of actions ?? []) {
    if (action.external_reference) {
      delete action.external_reference.status;
      action.external_reference.code = String(action.external_reference.code);
    }

    if (action.id) {
      update_actions.push(action);
    } else {
      new_actions.push(action);
    }
  }

  return put<UpdateActionsPayload & Partial<UpdateDiagnosisPayload>, AxiosResponse>(
    CASES_ACTIONS(id),
    {
      payload: {
        alarms,
        name,
        asset_id,
        owner_mail,
        status,
        is_able_to_operate,
        savings,
        new_actions,
        update_actions,
        severity,
        detection_method,
        diagnosis,
        dimension,
        discipline,
        anomaly_start,
      },
    },
  );
};

export const useGetAllActionsQuery = (id?: string) => {
  const response = useQuery(['GET_CASE_ACTIONS', id], get<ActionsArray>(CASES_ACTIONS(id)), {
    retry: false,
    enabled: !!id,
    select: (data: ActionsArray) => actionsFilter(data.actions),
  });

  return {
    ...response,
    data: response.data || [],
  };
};

const saveFeedback = ({
  alarms,
  asset_id,
  conclusion,
  feedback_comments,
  id,
  is_able_to_operate,
  name,
  owner_mail,
  savings,
  status,
  anomaly_end,
  severity,
  detection_method,
  diagnosis,
  dimension,
  discipline,
  anomaly_start,
}: CasePayload) => {
  return put<UpdateFeedbackPayload & UpdateDiagnosisPayload, AxiosResponse>(CASES_FEEDBACK(id), {
    payload: {
      alarms,
      asset_id,
      conclusion,
      feedback_comments,
      is_able_to_operate,
      name,
      owner_mail,
      savings,
      status,
      anomaly_end,
      severity,
      detection_method,
      diagnosis,
      dimension,
      discipline,
      anomaly_start,
    },
  });
};

export const useCreateCaseMutation = (
  onSuccess?: (data: AxiosResponse, variables: CasePayload) => void,
  onError?: () => void,
) => {
  return useMutation(createCase, {
    onSuccess,
    onError,
  });
};

export const useUpdateCaseMutation = (
  onSuccess?: (data: AxiosResponse, variables: Partial<CasePayload>) => void,
  onError?: () => void,
) =>
  useMutation(updateCase, {
    onSuccess,
    onError,
  });

export const useSaveActionsMutation = (
  onSuccess?: (data: AxiosResponse, variables: Partial<CasePayload>) => void,
  onError?: () => void,
) => useMutation(saveActions, { onSuccess, onError });

export const useSaveDiagnosisMutation = (
  id: string,
  onSuccess?: (data: AxiosResponse, variables: CasePayload) => void,
  onError?: () => void,
) =>
  useMutation(saveDiagnosis, {
    onSuccess,
    onError,
  });

export const useSaveFeedbackMutation = (
  id: string,
  onSuccess?: (data: AxiosResponse, variables: CasePayload) => void,
  onError?: () => void,
) =>
  useMutation(saveFeedback, {
    onSuccess,
    onError,
  });

export interface CaseAlarmsFilters {
  id?: string;
  asset_id?: string;
  start_date?: string;
  end_date?: string;
}

export interface CaseAlarmsFiltersParams
  extends Omit<CaseAlarmsFilters, 'start_date' | 'end_date'> {
  min_start_date?: string;
  min_end_date?: string;
  status?: string;
  classification?: string;
}

export interface RelatedAlarmsResponse {
  alarms: EventItem[];
  asset_alarms: EventItem[];
}

const getEventsFromAsset = async ({
  asset_id,
  id,
  ...params
}: CaseAlarmsFiltersParams): Promise<RelatedAlarmsResponse> => {
  const alarms = [] as EventItem[];

  if (!asset_id) {
    return { alarms, asset_alarms: [] };
  }

  const { events } = await get<{ events: EventItem[] }, CaseAlarmsFiltersParams>(
    `${ASSET_V2_ENDPOINT}s/${asset_id}/events`,
    params,
  )();

  return {
    alarms,
    asset_alarms: events,
  };
};

const getEventsFromAlarms = async ({
  id,
  ...params
}: CaseAlarmsFiltersParams): Promise<RelatedAlarmsResponse> => {
  const data = await get<RelatedAlarmsResponse, CaseAlarmsFiltersParams>(
    CASES_ALARMS(id),
    params,
  )();

  return {
    alarms: data?.alarms || [],
    asset_alarms: data?.asset_alarms || [],
  };
};

export const useCaseRelatedAlarms = (filters: CaseAlarmsFilters) => {
  const { id } = filters;

  const params = {
    id,
    asset_id: filters.asset_id,
    min_start_date: filters.start_date,
    max_start_date: filters.end_date,
    status: Status.CLOSED,
    classification: Classification.USEFUL,
  };

  const getEvents = id ? getEventsFromAlarms : getEventsFromAsset;

  const response = useQuery(['GET_CASE_ALARMS', filters], () => getEvents(params), {
    retry: false,
    enabled: !!filters.asset_id || !!id,
  });

  return {
    ...response,
    alarms: response.data?.alarms || [],
    asset_alarms: response.data?.asset_alarms || [],
  };
};

export const useGetCaseOptionsQuery = () => {
  return useQuery(['GET_ALL_OPTIONS'], get<CaseOptions>(CASES_OPTIONS()), {
    retry: false,
  });
};

export const useGetOptions = <T>(name: string, _: T) => {
  return useQuery(['GET_CASE_ACTIONS_OPTIONS', name], get<T>(TEMPLATE_OPTIONS(name)), {
    retry: false,
    enabled: !!name,
  });
};
export const useGetCasesFilterOptionsQuery = () => {
  return useQuery(
    ['CASES_FILTERS_OPTIONS'],
    get<CasesFilterOptionsResponse>(CASES_FILTERS_OPTIONS()),
    {
      retry: false,
    },
  );
};
export const useGetCasesOwnersQuery = () => {
  return useQuery(
    ['CASES_GET_OWNERS'],
    get<Pick<CasesFilterOptionsResponse, 'owners'>>(CASES_OWNERS),
    {
      retry: false,
    },
  );
};
